import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/sections/Footer';
import api from '../lib/api';
import { responsiveImg } from '../lib/imageUrl';
import { useText } from '../lib/siteContent';
import { PROJECT_CATEGORIES, categoryLabel } from '../lib/projects';

const reveal = (i) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: 0.9, delay: (i % 3) * 0.08 },
});

const GalleryItem = ({ img, i }) => (
  <motion.figure {...reveal(i)} data-testid={`realisation-item-${img.id}`}>
    <div className="aspect-[3/4] overflow-hidden bg-[#F5F5F5]">
      <img {...responsiveImg(img.imageUrl, '(max-width: 768px) 100vw, 33vw')} alt={img.title || ''} className="w-full h-full object-cover" />
    </div>
    <figcaption className="pt-5">
      {img.category && <span className="block text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B] mb-2">{img.category}</span>}
      {img.title && <span className="font-serif font-light text-xl text-[#111111]">{img.title}</span>}
      {img.description && <p className="mt-2 text-sm font-light leading-[1.8] text-[#333333]">{img.description}</p>}
    </figcaption>
  </motion.figure>
);

const ProjectCard = ({ p, i, t }) => (
  <motion.article {...reveal(i)} data-testid={`project-card-${p.slug}`}>
    <Link to={`/realisations/${p.slug}`} className="group block">
      <div className="aspect-[3/4] overflow-hidden bg-[#F5F5F5]">
        {(p.coverUrl || p.images?.[0]) && <img {...responsiveImg(p.coverUrl || p.images[0], '(max-width: 768px) 100vw, 50vw')} alt={p.title} className="w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.03]" />}
      </div>
      <span className="mt-5 block text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B]">{categoryLabel(t, p.category)}</span>
      <h2 className="mt-2 font-serif font-light text-2xl text-[#111111] leading-[1.2] group-hover:text-[#6B6B6B] transition-colors duration-500">{p.title}</h2>
      {p.location && <p className="mt-1 text-sm font-light text-[#333333]">{p.location}</p>}
    </Link>
  </motion.article>
);

const Filters = ({ active, counts, onSelect, t }) => (
  <div role="group" aria-label="Filtrer les réalisations" className="flex flex-wrap gap-x-8 gap-y-4 mb-16 md:mb-20 border-b border-[#111111]/10 pb-6" data-testid="realisations-filters">
    {['all', ...PROJECT_CATEGORIES].filter((k) => k === 'all' || counts[k]).map((k) => (
      <button key={k} type="button" onClick={() => onSelect(k)} aria-pressed={active === k} data-testid={`realisations-filter-${k}`}
        className={`text-[11px] uppercase tracking-[0.3em] pb-1 border-b transition-colors duration-300 ${active === k ? 'text-[#111111] border-[#111111]' : 'text-[#6B6B6B] border-transparent hover:text-[#111111]'}`}>
        {t(`realpage.filter.${k}`)}
      </button>
    ))}
  </div>
);

export default function RealisationsPage() {
  const [images, setImages] = useState(null);
  const [projects, setProjects] = useState(null);
  const [params, setParams] = useSearchParams();
  const t = useText();
  const active = PROJECT_CATEGORIES.includes(params.get('categorie')) ? params.get('categorie') : 'all';

  useEffect(() => {
    api.get('/projects').then(({ data }) => setProjects(data)).catch(() => setProjects([]));
    api.get('/gallery').then(({ data }) => setImages(data)).catch(() => setImages([]));
  }, []);

  const counts = (projects || []).reduce((acc, p) => ({ ...acc, [p.category]: (acc[p.category] || 0) + 1 }), {});
  const shown = (projects || []).filter((p) => active === 'all' || p.category === active);
  const hasProjects = projects && projects.length > 0;

  return (
    <div className="min-h-screen bg-[#FFFFFF] text-[#111111]" data-testid="realisations-page">
      <Navbar />
      <main className="container-custom pt-20 md:pt-32 pb-32">
        <span className="block text-[11px] uppercase tracking-[0.3em] text-[#6B6B6B] mb-8">{t('realpage.label')}</span>
        <h1 className="font-serif font-light text-[#111111] text-5xl sm:text-6xl lg:text-7xl leading-[1.02]" data-testid="realisations-page-title">
          {t('realpage.title')}
        </h1>
        <p className="mt-10 mb-20 md:mb-24 text-sm md:text-base leading-[1.9] font-light text-[#333333] max-w-xl whitespace-pre-line" data-testid="realisations-page-intro">{t('realpage.intro')}</p>
        {hasProjects && (
          <>
            <Filters active={active} counts={counts} t={t} onSelect={(k) => setParams(k === 'all' ? {} : { categorie: k }, { replace: true })} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 xl:gap-x-12 gap-y-16" data-testid="projects-grid">
              {shown.map((p, i) => <ProjectCard key={p.id} p={p} i={i} t={t} />)}
            </div>
          </>
        )}
        {projects && !hasProjects && images && images.length === 0 && (
          <p className="text-sm text-[#6B6B6B]" data-testid="realisations-empty">{t('realpage.empty')}</p>
        )}
        {projects && !hasProjects && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 xl:gap-x-12 gap-y-16" data-testid="realisations-grid">
            {(images || []).map((img, i) => <GalleryItem key={img.id} img={img} i={i} />)}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
